import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Calendar } from 'lucide-react';
import { format } from 'date-fns';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
  created_at: string;
  event_count: number;
  permissions?: string[];
}

interface StaffListTableProps {
  staff: StaffMember[];
  isLoading: boolean;
  onEdit: (staff: StaffMember) => void;
  onDelete: (staffId: string) => void;
}

const StaffListTable: React.FC<StaffListTableProps> = ({
  staff,
  isLoading,
  onEdit,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-muted-foreground">Loading staff members...</div>
      </div>
    );
  }

  if (staff.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No staff members found</h3>
        <p className="text-muted-foreground">
          Add your first staff member to get started
        </p>
      </div>
    );
  }

  const formatPermissions = (perms?: string[]) => {
    if (!perms || perms.length === 0) return '—';
    if (perms.includes('all')) return 'Full access';
    
    const permLabels: Record<string, string> = {
      check_in: 'Check-in',
      view_registrations: 'View',
      edit_registrations: 'Edit',
      manage_event: 'Manage',
      view_reports: 'Reports',
    };
    
    return perms.slice(0, 2).map(p => permLabels[p] || p).join(', ') + 
           (perms.length > 2 ? ` +${perms.length - 2}` : '');
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center"># Events</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {staff.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">{member.name}</TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{member.phone || '—'}</TableCell>
              <TableCell>
                <Badge variant={member.status === 'active' ? 'success' : 'secondary'}>
                  {member.status}
                </Badge>
              </TableCell>
              <TableCell className="text-center">
                <Badge variant="outline">{member.event_count}</Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatPermissions(member.permissions)}
              </TableCell>
              <TableCell>
                {format(new Date(member.created_at), 'MMM d, yyyy')}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(member)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(member.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default StaffListTable;
